/**
 * FilterRow Component
 *
 * Single filter row with field selector, operator selector, value input, and remove button.
 * Represents one filter condition in the filter builder.
 */

import { Space, Select, Button, Tooltip, Spin, Alert } from 'antd'
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { metadataService } from '@/services/metadataService'
import { getOperatorsForFieldType, getDefaultOperator, operatorRequiresValue, operatorRequiresMultipleValues, operatorRequiresRange } from '../operatorConfig'
import { DynamicValueInput } from './ValueInput/DynamicValueInput'
import type { FilterRowProps } from '../advancedFiltersTypes'

export function FilterRow({ filter, model, fields: passedFields, onChange, onRemove, baseFilters, hasLifecycle }: FilterRowProps) {
  // Only fetch field metadata if not passed from parent
  const { data: metadataResponse, isLoading: isMetadataLoading } = useQuery({
    queryKey: ['metadata', model, 'fields'],
    queryFn: async () => {
      const response = await metadataService.getNonSystemFields(model)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch metadata')
      }
      return response.data
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    // Skip fetch if fields are passed from parent
    enabled: !passedFields,
  })

  // Use passed fields or fetched fields
  const fields = passedFields ?? metadataResponse?.fields ?? {}
  const formSections = metadataResponse?.form_sections ?? []
  const isLoading = passedFields ? false : isMetadataLoading

  // Build field options grouped by form section
  const fieldOptions = Object.entries(fields)
    .filter(([_, fieldMeta]) => fieldMeta.is_visible !== false)
    .map(([key, fieldMeta]) => ({
      label: fieldMeta.title || key,
      value: key,
      formSection: fieldMeta.form_section,
    }))

  // Group by form section
  const groupedOptions = formSections.map((section) => ({
    label: section.name,
    options: fieldOptions.filter((opt) => opt.formSection === section.name),
  }))

  // Add ungrouped fields
  const ungroupedFields = fieldOptions.filter(
    (opt) => !opt.formSection || !formSections.find((s) => s.name === opt.formSection)
  )
  if (ungroupedFields.length > 0) {
    groupedOptions.push({
      label: 'Other Fields',
      options: ungroupedFields,
    })
  }

  // Get available operators for selected field
  const fieldMetadata = filter.field ? fields[filter.field] : null
  const fieldType = fieldMetadata?.py_type || 'string'
  const availableOperators = getOperatorsForFieldType(fieldType)

  // Handle field change
  const handleFieldChange = (newField: string) => {
    const newFieldMetadata = fields[newField]
    const newFieldType = newFieldMetadata?.py_type || 'string'
    const newDefaultOperator = getDefaultOperator(newFieldType)

    onChange({
      ...filter,
      field: newField,
      operator: newDefaultOperator.value,
      value: undefined,
      values: undefined,
      value2: undefined,
      error: undefined,
    })
  }

  // Handle operator change
  const handleOperatorChange = (newOperator: any) => {
    onChange({
      ...filter,
      operator: newOperator,
      value: undefined,
      values: undefined,
      value2: undefined,
      error: undefined,
    })
  }

  // Validate filter row
  const getValidationError = (): string | null => {
    if (!filter.field) {
      return 'Please select a field'
    }
    if (!filter.operator) {
      return 'Please select an operator'
    }

    // Check if operator requires value
    if (operatorRequiresValue(filter.operator)) {
      if (filter.value === undefined || filter.value === null || filter.value === '') {
        return 'Please provide a value'
      }
    }

    // Check if operator requires multiple values
    if (operatorRequiresMultipleValues(filter.operator)) {
      if (!filter.values || filter.values.length === 0) {
        return 'Please provide at least one value'
      }
    }

    // Check if operator requires range
    if (operatorRequiresRange(filter.operator)) {
      if (filter.value === undefined || filter.value === null || filter.value === '') {
        return 'Please provide a start value'
      }
      if (filter.value2 === undefined || filter.value2 === null || filter.value2 === '') {
        return 'Please provide an end value'
      }
    }

    return null
  }

  const validationError = getValidationError()
  const hasError = validationError !== null

  // Show loading spinner while metadata loads
  if (isLoading) {
    return (
      <Space direction="horizontal" size="small" style={{ width: '100%', marginBottom: 8 }}>
        <Spin size="small" />
        <span style={{ color: '#8c8c8c' }}>Loading fields...</span>
      </Space>
    )
  }

  return (
    <div style={{ width: '100%', marginBottom: 8 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '180px 140px 1fr auto auto',
          gap: '8px',
          alignItems: 'center',
          width: '100%',
          padding: '8px',
          border: hasError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
          borderRadius: '4px',
          backgroundColor: hasError ? '#fff2f0' : '#fafafa',
        }}
      >
        {/* Field Selector */}
        <Select
          placeholder="Select field"
          value={filter.field || undefined}
          onChange={handleFieldChange}
          showSearch
          filterOption={(input, option) =>
            (option?.label?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
          }
          options={groupedOptions}
          status={!filter.field ? 'error' : undefined}
        />

        {/* Operator Selector */}
        <Select
          placeholder="Operator"
          value={filter.operator}
          onChange={handleOperatorChange}
          disabled={!filter.field}
          status={!filter.operator ? 'error' : undefined}
        >
          {availableOperators.map((op) => (
            <Select.Option key={op.value} value={op.value}>
              {op.label}
            </Select.Option>
          ))}
        </Select>

        {/* Value Input */}
        <div style={{ minWidth: 0 }}>
          <DynamicValueInput
            field={filter.field}
            model={model}
            fields={passedFields}
            operator={filter.operator}
            value={filter.value}
            values={filter.values}
            value2={filter.value2}
            onChange={(updates) => onChange({ ...filter, ...updates })}
            baseFilters={baseFilters}
            hasLifecycle={hasLifecycle}
          />
        </div>

        {/* Error Icon */}
        {hasError && (
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
        )}

        {/* Remove Button */}
        <Tooltip title="Remove filter">
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onRemove}
            danger
          />
        </Tooltip>
      </div>

      {/* Error Message */}
      {hasError && (
        <Alert
          message={validationError}
          type="error"
          showIcon
          style={{ marginTop: 4 }}
          closable={false}
        />
      )}
    </div>
  )
}